import { useEffect, useState } from 'react';
import { getPlanReturns } from '../api/plan-returns/getPlanReturns';
import { toTransferReturns, TransferReturns } from '../utils/transferReturns';
import { validYearsToRetirement } from '../utils/monthlyProjection';

/**
 * Rentabilidades de un traspaso: las decide comparsa (`/plan-returns`, escenario TRANSFER) para
 * el plan de Arca elegido frente al del usuario, en el periodo común más largo. Nada se decide
 * aquí: ni periodo, ni suavizado, ni tasas de respaldo.
 *
 * `undefined` mientras llega; `null` si falla o no hay nada que pedir. Si falla con el plan del
 * usuario (p. ej. una recarga del registro lo quitó: 404), se reintenta sin él: el plan de Arca
 * se sigue enseñando y el del usuario sale sin datos. Al cambiar de plan, la respuesta anterior
 * se descarta.
 *
 * @param originDgsfp Código del plan del usuario en el registro (DGSFP o PPA), solo si es válido
 * (`originPlanCode`).
 * @param yearsToRetirementRaw Años hasta la jubilación (`yearsToRetirementFor`): con ellos, los
 *   planes de ciclo de vida llegan con sus tramos (chirigota#592). Sin ellos, sin tramos.
 */
const useTransferReturns = (
	token: string | undefined,
	arcaSlug: string | undefined,
	originDgsfp: string | undefined,
	yearsToRetirementRaw?: number
): TransferReturns | null | undefined => {
	const yearsToRetirement = validYearsToRetirement(yearsToRetirementRaw);
	// La respuesta se guarda con la consulta que la pidió: si la consulta cambia (llega el plan
	// tarde, o se elige otro), ni un render enseña `null` ni la respuesta del plan anterior.
	const key =
		token && arcaSlug
			? `${token}|${arcaSlug}|${originDgsfp ?? ''}|${yearsToRetirement ?? ''}`
			: null;
	const [state, setState] = useState<{ key: string; value: TransferReturns | null } | null>(null);

	useEffect(() => {
		if (!token || !arcaSlug || !key) return;
		let cancelled = false;
		(async () => {
			let res = await getPlanReturns(token, {
				arcaPlans: [arcaSlug],
				originDgsfp,
				yearsToRetirement
			});
			if (!res && originDgsfp) {
				res = await getPlanReturns(token, { arcaPlans: [arcaSlug], yearsToRetirement });
			}
			if (!cancelled) setState({ key, value: res ? toTransferReturns(res) : null });
		})();
		return () => {
			cancelled = true;
		};
	}, [key, token, arcaSlug, originDgsfp, yearsToRetirement]);

	if (!key) return null;
	return state?.key === key ? state.value : undefined;
};

export default useTransferReturns;
